import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  Button,
  Checkbox,
  Collapse,
  Icon,
  TableCell,
  TableRow
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import { DesignedConfirmDialog, TextFormattedNumber } from 'components';
import {
  orderStatus,
  statusMessages,
  getIntegrationStatus
} from 'helpers/orders';
import { updateStoreOrderApi, getOrderProductsBySkusApi } from 'api';
import { convertUTCDateToLocalDate } from 'helpers/date';
import Order from './Order';


const useStyles = makeStyles(() => ({
  blueCell: {
    backgroundColor: '#E7F0FF',
    borderTop: '1px solid #D0E9FF',

    '&.MuiTableCell-root': {
      tableLayout: 'auto',
      maxWidth: '1000px',
    },
  },
  noBorders: {
    '& > *': {
      borderBottom: 'unset'
    },
  },
  noWrap: {
    whiteSpace: 'nowrap'
  }
}));

const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const OrderRowCollapsible = ({order, isItemSelected, labelId, handleSelectRow, headTableCells}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [openRow, setOpenRow] = useState(false);
  const [isOpenConfirmDialog, toggleOpenConfirmDialog] = useState(false);
  const [postStatus, setPostStatus] = useState(false);
  const {
    increment_id,
    entity_id,
    customer_firstname,
    customer_lastname,
    customer_email,
    customer_group_id,
    status,
    created_at,
    grand_total,
    billing_address: {
      firstname,
      lastname
    },
    extension_attributes: {
      delivery_status,
      dermpro_fraud_score,
      survey_question_value
    }
  } = order;

  const columns = headTableCells.reduce((acc, column) => {
    acc[column.label] = column;

    return acc;
  }, {});

  const { customerGroups } = useSelector((state: IReduxState) => state.customers);

  const nextechStatus = getIntegrationStatus(order, 'nextech');
  const modmedStatus = getIntegrationStatus(order, 'modmed');
  const date = convertUTCDateToLocalDate(created_at);
  const formattedPurchaseDate = format(date, 'MM/dd/yyyy');
  const formattedPurchaseHours = format(date, 'hh:mm:ss aa');


  const handleChangePostStatus = () => {
    const order = {
      increment_id,
      entity_id,
      extension_attributes: {
        delivery_status: postStatus ? '1' : '0'
      }
    };
    updateStoreOrderApi({dispatch, order});
  };

  const handleToggleOrderDetails = (event) => {
    const skus = order.items.map(({ sku }) => sku) || [];
    getOrderProductsBySkusApi({dispatch, skus});

    event.preventDefault();
    event.stopPropagation();
    setOpenRow(!openRow);
  };

  const customerGroupName = [...customerGroups, ...defaultCustomerGroups]
    .find((group) => group.id == customer_group_id)?.code || '---';

  return (
    <>
      <TableRow
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        selected={isItemSelected}
        className={`${openRow ? classes.blueCell : ''} ${classes.noBorders}`}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isItemSelected}
            onClick={(event) => handleSelectRow(event, increment_id)}
            color="primary"
            inputProps={{ 'aria-labelledby': labelId }}
          />
        </TableCell>
        {columns['Order Id']?.display &&
          <TableCell component="th" id={labelId} scope="row" padding="none">
            <strong className="pr-2 whitespace-no-wrap">{increment_id}</strong>
          </TableCell>
        }
        {columns['Purchase Date']?.display &&
          <TableCell>
            <div>{formattedPurchaseDate}</div>
            <div>{formattedPurchaseHours}</div>
          </TableCell>
        }
        {columns['Order Amount']?.display &&
          <TableCell>
            <TextFormattedNumber
              value={grand_total}
              prefix="$"
            />
          </TableCell>
        }
        {columns['First Name']?.display && <TableCell>{customer_firstname ? customer_firstname : firstname} </TableCell>}
        {columns['Last Name']?.display && <TableCell>{customer_lastname ? customer_lastname : lastname} </TableCell>}
        {columns['Nextech status']?.display && <TableCell>{statusMessages[nextechStatus]}</TableCell>}
        {columns['Mod Med status']?.display && <TableCell>{statusMessages[modmedStatus]}</TableCell>}
        {columns['Email']?.display && <TableCell>{customer_email}</TableCell>}
        {columns['Customer Group']?.display && <TableCell>{customerGroupName}</TableCell>}
        {columns['Status']?.display &&
          <TableCell>
            <span className={`whitespace-no-wrap ${status} ${status ? 'indicator' : null}`}>
              {status ? orderStatus[status] : ''}
            </span>
          </TableCell>
        }
        {columns['Survey']?.display &&
          <TableCell>
            {survey_question_value}
          </TableCell>}
        {columns['Posted']?.display &&
          <TableCell align="left">
            <Checkbox
              color="primary"
              checked={delivery_status == 1}
              onChange={({target: {checked}}) => {
                setPostStatus(checked);
                toggleOpenConfirmDialog(true);
              }}
            />
          </TableCell>
        }
        {columns['Risk Score']?.display &&
          <TableCell>
            {dermpro_fraud_score && (
              <span className={dermpro_fraud_score > 5 ? 'text-error' : ''}>
                {dermpro_fraud_score}%
              </span>
            )}
          </TableCell>
        }
        {columns['Details']?.display &&
          <TableCell align="left">
            <Button
              className="p-0 whitespace-no-wrap"
              color="primary"
              onClick={(event) => handleToggleOrderDetails(event)}
            >
              <Icon className="mr-2" fontSize="small">
                {`${openRow ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}`}
              </Icon>
              {`${openRow ? 'View Less' : 'View More'}`}
            </Button>
          </TableCell>
        }
      </TableRow>
      <TableRow>
        <TableCell colSpan={Object.keys(headTableCells).length + 1} padding="none" className={`${openRow ? classes.blueCell : ''}`}>
          <Collapse in={openRow} timeout="auto" unmountOnExit>
            <Order order={order} />
          </Collapse>
        </TableCell>
      </TableRow>
      <DesignedConfirmDialog
        open={isOpenConfirmDialog}
        setOpen={toggleOpenConfirmDialog}
        onConfirm={handleChangePostStatus}
      >
        {`Do you want ${postStatus ? 'POST' :  'UNPOST'} this order?`}
      </DesignedConfirmDialog>
    </>
  );
};

export default OrderRowCollapsible;
